import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiClock } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { roundStartInstant } from '../utils/timeHelpers';

function RoundsToggle({
  rounds = [],
  groupId = null,
  activeTab = 'upcoming',
  onTabChange,
}) {
  // Filter and process rounds
  const { upcomingRounds, pastRounds, pendingInvitationsCount } = useMemo(() => {

    // Filter by groupId if provided
    let filteredRounds = rounds;
    if (groupId) {
      filteredRounds = rounds.filter(round => round.group_id === groupId);
    }

    // Bucket by round start instant (date + time + tz), not raw date string.
    // `new Date(round.date)` parses YYYY-MM-DD as UTC midnight, which shifts
    // back one calendar day for any viewer west of UTC — so a UK round shown
    // to a US viewer was getting mis-classified at the day boundary.
    const nowMs = Date.now();
    const upcoming = filteredRounds.filter(round => {
      const i = roundStartInstant(round);
      return i ? i.getTime() >= nowMs : false;
    });
    const past = filteredRounds.filter(round => {
      const i = roundStartInstant(round);
      return i ? i.getTime() < nowMs : false;
    });

    // Count pending invitations in upcoming rounds
    const pendingCount = upcoming.filter(round => 
      round.user_invitation_status === 'pending'
    ).length;


    return {
      upcomingRounds: upcoming,
      pastRounds: past,
      pendingInvitationsCount: pendingCount
    };
  }, [rounds, groupId]);

  return (
    <div className="w-full xs:w-auto md:w-auto">
      {/* Toggle Buttons */}
      <div className="flex space-x-1 bg-surface rounded-lg p-1 w-full xs:w-fit md:w-fit">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onTabChange('upcoming')}
          className={`relative flex items-center justify-center flex-1 xs:flex-initial md:flex-initial px-3 xs:px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
            activeTab === 'upcoming'
              ? 'bg-card text-accent '
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1 xs:mr-2" />
          <span className="hidden xs:inline">Upcoming Rounds</span>
          <span className="xs:hidden">Upcoming</span>
          
          {/* Pending Invitations Badge */}
          {pendingInvitationsCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-status-error text-white text-xs font-bold rounded-full flex items-center justify-center"
            >
              {pendingInvitationsCount}
            </motion.div>
          )}
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onTabChange('past')}
          className={`flex items-center justify-center flex-1 xs:flex-initial md:flex-initial px-3 xs:px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
            activeTab === 'past'
              ? 'bg-card text-accent '
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          <SafeIcon icon={FiClock} className="w-4 h-4 mr-1 xs:mr-2" />
          <span className="hidden xs:inline">Past Rounds</span>
          <span className="xs:hidden">Past</span>
        </motion.button>
      </div>
    </div>
  );
}

export default RoundsToggle;